import type { JSX } from 'react';
import { useMemo } from 'react';

import { EditOutlineIcon, LinkIcon, ShareNowIcon, VisibilityIcon } from '@change/ds-icons';

import { Button } from '@change/ds-core-ui/next';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import type { ActionButtonProps } from '../../../../types';

import { useActionButtons } from './hooks/useActionButtons';

type ButtonCustomProps = {
	icon: JSX.Element;
	handleClick: () => void | Promise<void>;
	id: string;
	label: string;
};

export const SHARE_BUTTON_VARIATION_STYLES =
	'!text-typography-inverse-strong !bg-[#E01A2B] before:!border-none active:!bg-[#B71523] before:active:!border-2 before:active:!border-solid before:active:!border-black hover:!bg-[#B71523] before:hover:!border-2 before:hover:!border-solid before:hover:!border-black';

export function ActionButton({ type, mobile = false }: ActionButtonProps): JSX.Element {
	const {
		actions: { handleCopyLinkClick, handleEditPetitionClick, handleViewPetitionClick, handleSharePetitionClick },
	} = useActionButtons();
	const { translate } = useI18n();
	const { icon, handleClick, id, label } = useMemo((): ButtonCustomProps => {
		switch (type) {
			case 'edit':
				return {
					icon: <EditOutlineIcon aria-label="edit-icon" />,
					handleClick: handleEditPetitionClick,
					id: 'edit-header-link',
					label: 'edit_petition_link',
				};
			case 'link':
				return {
					icon: <LinkIcon aria-label="link-icon" />,
					handleClick: handleCopyLinkClick,
					id: 'copy-link-header-button',
					label: 'copy_link_button.default_state',
				};
			case 'view':
				return {
					icon: <VisibilityIcon aria-label="view-icon" />,
					handleClick: handleViewPetitionClick,
					id: 'petition-details-header-link',
					label: 'view_petition_live_link',
				};
			case 'share':
			case 'shareVariant':
			default:
				return {
					icon: <ShareNowIcon aria-label="share-icon" />,
					handleClick: handleSharePetitionClick,
					id: 'share-link-header-button',
					label: 'share_link_button',
				};
		}
	}, [type, handleCopyLinkClick, handleEditPetitionClick, handleViewPetitionClick, handleSharePetitionClick]);

	const shareButtonStyleOverrides = type === 'shareVariant' ? SHARE_BUTTON_VARIATION_STYLES : '';

	return (
		<Button
			key={type}
			data-qa={id}
			data-testid={id}
			leadingIcon={icon}
			onClick={handleClick}
			size={mobile ? 'x-small' : 'small'}
			variant={type === 'share' ? 'primary' : 'secondary'}
			className={`rounded-medium px-[var(--space-4,16px)] py-[8px] ${mobile ? '[&>*]:text-size-body-xs flex-1 flex-col gap-0.5 py-[var(--space-4,16px)] [&>*]:leading-4' : 'h-[36px] min-w-[104px] [&>*]:text-size-body-small [&>*]:font-bold'} ${shareButtonStyleOverrides}`}
			aria-label={translate(
				`corgi.petition.starter_dashboard.header_navigation.${type === 'view' ? 'view_petition_live_link_a11y' : label}`,
			)}
		>
			<Translate value={`corgi.petition.starter_dashboard.header_navigation.${label}`} />
		</Button>
	);
}
